import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import {
  Button,
  DatePicker,
  EllipsisSpan,
  Form,
  Input,
  message,
  Modal,
  Select,
  Table,
  useCurrent,
} from '@maxtropy/components';
import {
  apiV2AreaGetValveSelectPost,
  apiV2AreaList2Post,
  apiV2AreaTaskAutoControlConfirmationPost,
  apiV2AreaTaskControlConfirmPost,
  apiV2AreaTaskListRerunPost,
  apiV2AreaTaskGetConfirmStationListPost,
  apiV2AreaTaskSendConfirmCodePost,
  V2AreaTaskAutoControlConfirmationPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { Col, Row, Space, type FormInstance } from 'antd';
import { AutoControlStatus } from './AutoControlConfirmLogModal';
import styles from '../index.module.scss';

const { RangePicker } = DatePicker;
const VERIFY_CODE_COUNTDOWN = 5 * 60;
const MAX_CONTROL_RANGE_DAYS = 31;

interface AutoControlConfirmModalProps {
  open: boolean;
  onCancel: () => void;
  onHandled: () => void;
}

interface FilterParams {
  areaId?: number;
  valveId?: number;
  operateRange?: [Dayjs, Dayjs];
  stationRooms?: number[];
}

type CalendarRange = [Dayjs | null, Dayjs | null] | null;

interface VerifyCodeFormValues {
  verifyCode?: string;
}

interface VerifyCodeConfirmContentProps {
  actionText: string;
  actionTip: string;
  form: FormInstance<VerifyCodeFormValues>;
  phone?: string;
}

type AutoControlConfirmItem = Exclude<V2AreaTaskAutoControlConfirmationPostResponse['list'], undefined>[number];

const getDefaultFilterParams = (): FilterParams => ({
  operateRange: [dayjs().startOf('day'), dayjs().add(1, 'day').startOf('day')],
});

const isControlRangeValid = (range?: CalendarRange) => {
  if (!range?.[0] || !range[1]) return true;
  return range[1].startOf('day').diff(range[0].startOf('day'), 'day') < MAX_CONTROL_RANGE_DAYS;
};

const formatCountdown = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const restSeconds = seconds % 60;
  return `${minutes}分${restSeconds}秒`;
};

const VerifyCodeConfirmContent: React.FC<VerifyCodeConfirmContentProps> = ({ actionText, actionTip, form, phone }) => {
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => {
      setCountdown(value => Math.max(value - 1, 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  const sendVerifyCode = () => {
    if (!phone) {
      message.warning('当前员工未配置手机号，无法获取验证码');
      return;
    }

    setSending(true);
    apiV2AreaTaskSendConfirmCodePost({ key: phone })
      .then(() => {
        message.success('验证码已发送');
        setCountdown(VERIFY_CODE_COUNTDOWN);
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        您确定一键{actionText}执行选中阀门的控制指令吗，{actionTip}
        ，只能前往组态页面手动开关阀门，并且记录操作日志。
      </div>
      <Form<VerifyCodeFormValues> form={form} isOrigin={true}>
        <Row gutter={8}>
          <Col span={16}>
            <Form.Item
              name="verifyCode"
              style={{ flex: 1, marginBottom: 0 }}
              rules={[{ required: true, message: '请输入验证码' }]}
            >
              <Input placeholder="请输入验证码，验证码5分钟内有效" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Button loading={sending} disabled={countdown > 0} onClick={sendVerifyCode}>
              {countdown > 0 ? formatCountdown(countdown) : '获取验证码'}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

const AutoControlConfirmModal: React.FC<AutoControlConfirmModalProps> = ({ open, onCancel, onHandled }) => {
  const [form] = Form.useForm<FilterParams>();
  const [verifyForm] = Form.useForm<VerifyCodeFormValues>();
  const current = useCurrent();
  const [searchParams, setSearchParams] = useState<FilterParams>(() => getDefaultFilterParams());
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectingOperateRange, setSelectingOperateRange] = useState<CalendarRange>(null);
  const [rerunLoading, setRerunLoading] = useState(false);

  const selectedAreaId = Form.useWatch('areaId', form);

  const { data: areaList } = useRequest(() => apiV2AreaList2Post().then(res => res.list ?? []), {
    ready: open,
  });

  const { data: scadaSelectData } = useRequest(
    () => apiV2AreaTaskGetConfirmStationListPost().then(res => res.list ?? []),
    {
      ready: open,
    }
  );

  const { data: valveSelectData } = useRequest(
    () => apiV2AreaGetValveSelectPost({ areaId: selectedAreaId }).then(res => res.list ?? []),
    {
      ready: open,
      refreshDeps: [selectedAreaId],
    }
  );

  const areaOptions = useMemo(
    () =>
      (areaList ?? []).filter(item => item.id !== undefined).map(item => ({ label: item.zoneName, value: item.id! })),
    [areaList]
  );

  const valveOptions = useMemo(() => {
    return (valveSelectData ?? [])?.map(item => ({ label: item.name, value: item.id! }));
  }, [valveSelectData]);

  const stationOptions = useMemo(
    () => (scadaSelectData ?? []).map(item => ({ label: item.name, value: item.id! })),
    [scadaSelectData]
  );

  const {
    data: tableData,
    loading,
    refresh,
  } = useRequest(
    async () => {
      const response = await apiV2AreaTaskAutoControlConfirmationPost({
        areaId: searchParams.areaId,
        valveId: searchParams.valveId,
        startTime: searchParams.operateRange?.[0] ? dayjs(searchParams.operateRange[0]).valueOf() : undefined,
        endTime: searchParams.operateRange?.[1] ? dayjs(searchParams.operateRange[1]).valueOf() : undefined,
        stationIds: searchParams.stationRooms,
      });
      return response?.list ?? [];
    },
    {
      ready: open,
      refreshDeps: [open, searchParams],
    }
  );

  const scadaIdRowSpanMap = useMemo(() => {
    const rowSpanMap = new Map<number, number>();
    const rows = tableData ?? [];
    let startIndex = 0;

    while (startIndex < rows.length) {
      const currentScadaId = rows[startIndex].scadaId;
      if (currentScadaId === undefined || currentScadaId === null) {
        rowSpanMap.set(startIndex, 1);
        startIndex += 1;
        continue;
      }

      let endIndex = startIndex + 1;
      while (endIndex < rows.length && rows[endIndex].scadaId === currentScadaId) {
        endIndex += 1;
      }

      rowSpanMap.set(startIndex, endIndex - startIndex);
      for (let i = startIndex + 1; i < endIndex; i += 1) {
        rowSpanMap.set(i, 0);
      }
      startIndex = endIndex;
    }

    return rowSpanMap;
  }, [tableData]);

  const columns = useMemo(
    () => [
      {
        title: '站房',
        dataIndex: 'scadaName',
        width: 140,
        ellipsis: true,
        onCell: (_: AutoControlConfirmItem, index?: number) => ({
          rowSpan: scadaIdRowSpanMap.get(index ?? -1) ?? 1,
        }),
        render: (value?: string) => <EllipsisSpan value={value || '--'} />,
      },
      {
        title: '供气阀门',
        dataIndex: 'valveName',
        width: 120,
        ellipsis: true,
        render: (value?: string) => <EllipsisSpan value={value} />,
      },
      {
        title: '供气区域',
        dataIndex: 'areaName',
        width: 140,
        ellipsis: true,
        render: (value?: string) => <EllipsisSpan value={value} />,
      },
      {
        title: '控制指令',
        dataIndex: 'commands',
        width: 200,
        ellipsis: true,
        render: (value?: string[]) => <EllipsisSpan value={value?.join('；') || '--'} />,
      },
      {
        title: '控制时间',
        dataIndex: 'executionTime',
        width: 150,
        ellipsis: true,
        render: (value?: string) => {
          const parsed = value ? dayjs(value) : undefined;
          return <EllipsisSpan value={parsed?.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : '--'} />;
        },
      },
    ],
    [scadaIdRowSpanMap]
  );

  const resetFilters = useCallback(() => {
    form.resetFields();
    const defaultValues = getDefaultFilterParams();
    form.setFieldsValue(defaultValues);
    setSearchParams(defaultValues);
    setSelectedRowKeys([]);
    setSelectingOperateRange(null);
  }, [form]);

  useEffect(() => {
    if (!open) {
      resetFilters();
    }
  }, [open]);

  const handleBatchControl = (status: AutoControlStatus.Confirmed | AutoControlStatus.Canceled) => {
    const ids = selectedRowKeys.map(Number).filter(id => Number.isFinite(id));
    if (!ids.length) {
      message.warning('请先勾选未确认指令');
      return;
    }

    const actionText = status === AutoControlStatus.Confirmed ? '确认' : '取消';
    const actionTip = status === AutoControlStatus.Confirmed ? '确认后将无法再次取消执行' : '取消后将无法再次确认执行';
    const phone = current?.customerUser.cellphone;
    verifyForm.resetFields();
    onCancel();
    Modal.confirm({
      title: `一键${actionText}提示`,
      content: (
        <VerifyCodeConfirmContent actionText={actionText} actionTip={actionTip} form={verifyForm} phone={phone} />
      ),
      async onOk() {
        const values = await verifyForm.validateFields();
        await apiV2AreaTaskControlConfirmPost({
          ids,
          status,
          phone,
          verifyCode: values.verifyCode,
        });
        message.success(`一键${status === AutoControlStatus.Confirmed ? '确认执行' : '取消执行'}成功`);
        setSelectedRowKeys([]);
        refresh();
        onHandled();
      },
      onCancel() {
        verifyForm.resetFields();
      },
    });
  };

  const handleRerunConfirmList = async () => {
    if (rerunLoading) return;

    const values = await form.validateFields(['operateRange']);
    const operateRange = values.operateRange ?? searchParams.operateRange;
    setRerunLoading(true);
    apiV2AreaTaskListRerunPost({
      executionStartTime: operateRange?.[0] ? dayjs(operateRange[0]).valueOf() : undefined,
      executionEndTime: operateRange?.[1] ? dayjs(operateRange[1]).valueOf() : undefined,
    })
      .then(() => {
        message.success('重新生成确认单成功');
        setSelectedRowKeys([]);
        refresh();
      })
      .finally(() => setRerunLoading(false));
  };

  const handleClose = () => {
    setSelectedRowKeys([]);
    onCancel();
  };

  const onValuesChange = (_: Partial<FilterParams>, allValues: FilterParams) => {
    if (!isControlRangeValid(allValues.operateRange)) return;
    setSearchParams(allValues);
  };

  const disabledOperateDate = (current: Dayjs) => {
    const start = selectingOperateRange?.[0];
    const end = selectingOperateRange?.[1];
    const tooLate = start && current.startOf('day').diff(start.startOf('day'), 'day') >= MAX_CONTROL_RANGE_DAYS;
    const tooEarly = end && end.startOf('day').diff(current.startOf('day'), 'day') >= MAX_CONTROL_RANGE_DAYS;
    return !!tooLate || !!tooEarly;
  };

  return (
    <Modal
      title="自动控制确认单"
      open={open}
      size="big"
      onCancel={handleClose}
      bodyScroll
      footer={[
        <Button key="close" onClick={handleClose}>
          关闭
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Form<FilterParams>
          form={form}
          initialValues={getDefaultFilterParams()}
          onValuesChange={onValuesChange}
          isOrigin={true}
          className={styles.autoForm}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="areaId" label="供气区域">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder="请选择供气区域"
                  options={areaOptions}
                  onChange={() => {
                    form.setFieldValue('valveId', undefined);
                    setSearchParams({ ...form.getFieldsValue(), valveId: undefined });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="valveId" label="供气阀门">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder="请选择供气阀门"
                  options={valveOptions}
                />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item
                name="operateRange"
                label="控制时间"
                rules={[
                  {
                    validator: (_, value: CalendarRange) =>
                      isControlRangeValid(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error('控制时间范围最多选择31天')),
                  },
                ]}
              >
                <RangePicker
                  style={{ width: '100%' }}
                  allowClear
                  showTime={{
                    format: 'HH',
                    showMinute: false,
                    showSecond: false,
                  }}
                  format="YYYY-MM-DD HH"
                  disabledDate={disabledOperateDate}
                  onCalendarChange={dates => setSelectingOperateRange(dates as CalendarRange)}
                  onOpenChange={pickerOpen => {
                    if (!pickerOpen) setSelectingOperateRange(null);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="stationRooms" label="站房">
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder="请选择站房"
                  options={stationOptions}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space size={8}>
            <Button
              type="primary"
              disabled={!selectedRowKeys.length}
              onClick={() => handleBatchControl(AutoControlStatus.Confirmed)}
            >
              一键确认
            </Button>
            <Button
              danger
              disabled={!selectedRowKeys.length}
              onClick={() => handleBatchControl(AutoControlStatus.Canceled)}
            >
              一键取消
            </Button>
            <Button loading={rerunLoading} onClick={handleRerunConfirmList}>
              重新生成确认单
            </Button>
          </Space>
        </div>

        <Table
          rowKey={record => record.id ?? `${record.areaId}-${record.valveId}-${record.executionTime}`}
          columns={columns}
          dataSource={tableData ?? []}
          loading={loading}
          pagination={false}
          rowSelection={{
            selectedRowKeys,
            onChange: keys => setSelectedRowKeys(keys),
          }}
        />
      </div>
    </Modal>
  );
};

export default AutoControlConfirmModal;
